import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { initializeGame, moveUp, moveDown, moveLeft, moveRight, checkGameOver, addRandomTile } from '../utils/gameLogic';
import { saveGameState, loadGameState } from '../utils/localStorage';

const GameContext = createContext();

const initialState = {
  board: [],
  score: 0,
  bestScore: 0,
  gameOver: false,
  won: false,
  keepPlaying: false,
  lastMove: null,
  moved: false
};

function gameReducer(state, action) {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        board: action.payload.board,
        score: 0,
        gameOver: false,
        won: false,
        keepPlaying: false,
        lastMove: null,
        moved: false
      };
    case 'LOAD_SAVED_STATE':
      return {
        ...action.payload
      };
    case 'MOVE':
      const { board, scoreIncrease, moved } = action.payload;
      const newScore = state.score + scoreIncrease;
      const bestScore = Math.max(state.bestScore, newScore);

      // Check if player has won (reached 2048 tile)
      const won = !state.won && !state.keepPlaying &&
        board.some(row => row.some(cell => cell && cell.value === 2048));

      return {
        ...state,
        board,
        score: newScore,
        bestScore,
        lastMove: action.direction,
        moved,
        won: won,
        gameOver: !moved && checkGameOver(board)
      };
    case 'KEEP_PLAYING':
      return {
        ...state,
        keepPlaying: true
      };
    case 'RESTART':
      return {
        ...state,
        board: action.payload.board,
        score: 0,
        gameOver: false,
        won: false,
        keepPlaying: false,
        lastMove: null,
        moved: false
      };
    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  // Load saved game on initial render
  useEffect(() => {
    const savedState = loadGameState();
    if (savedState) {
      dispatch({ type: 'LOAD_SAVED_STATE', payload: savedState });
    } else {
      const newBoard = initializeGame();
      dispatch({ type: 'INITIALIZE', payload: { board: newBoard } });
    }
  }, []);

  // Save game state whenever it changes
  useEffect(() => {
    if (state.board.length > 0) {
      saveGameState(state);
    }
  }, [state]);

  const moveBoard = (direction) => {
    if (state.gameOver) return;

    let newBoard = JSON.parse(JSON.stringify(state.board));
    let scoreIncrease = 0;
    let moved = false;

    switch (direction) {
      case 'up':
        ({ board: newBoard, score: scoreIncrease, moved } = moveUp(newBoard));
        break;
      case 'down':
        ({ board: newBoard, score: scoreIncrease, moved } = moveDown(newBoard));
        break;
      case 'left':
        ({ board: newBoard, score: scoreIncrease, moved } = moveLeft(newBoard));
        break;
      case 'right':
        ({ board: newBoard, score: scoreIncrease, moved } = moveRight(newBoard));
        break;
      default:
        break;
    }

    if (moved) {
      newBoard = addRandomTile(newBoard);
    }

    dispatch({
      type: 'MOVE',
      payload: { board: newBoard, scoreIncrease, moved },
      direction
    });
  };

  const restartGame = () => {
    const newBoard = initializeGame();
    dispatch({ type: 'RESTART', payload: { board: newBoard } });
  };

  const keepPlaying = () => {
    dispatch({ type: 'KEEP_PLAYING' });
  };

  return (
    <GameContext.Provider value={{
      ...state,
      moveBoard,
      restartGame,
      keepPlaying
    }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  return useContext(GameContext);
}